/** Removing an operator from a campaign, and deciding where their
 *  outstanding work goes.
 *
 *  Asked for 2026-09-28. Removal used to be a one-click delete of the
 *  assignment row, which silently orphaned whatever was booked with
 *  that operator: those sessions kept pointing at somebody no longer on
 *  the campaign, so they disappeared from the operators tab while still
 *  sitting in the beneficiary's calendar.
 *
 *  The dialog only asks when there is something to ask about — an
 *  operator with nothing outstanding is removed without ceremony.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDateTime } from "@/lib/format";

import { getAssigneeWork, removeAssignee } from "./assigneesApi";

type HandoverClash = {
  session_id: string;
  scheduled_for: string;
  beneficiary: string;
};

/** The appointments that would land on top of a call the receiving
 *  operator already has — the API refuses the whole hand-over and
 *  names them (2026-10-18; it used to move them blind). */
function handoverClashes(err: unknown): HandoverClash[] | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) return null;
  const data = err.response.data as
    | { detail?: string; clashes?: HandoverClash[] }
    | undefined;
  if (data?.detail !== "handover_would_double_book") return null;
  return data.clashes ?? [];
}

export function RemoveOperatorModal({
  campaignId,
  userId,
  displayName,
  candidates,
  onClose,
}: {
  campaignId: string;
  userId: number;
  displayName: string;
  /** Bookable assignees who could take the work — never the leaver. */
  candidates: { user_id: number; display_name: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [target, setTarget] = useState<number | "">("");

  const work = useQuery({
    queryKey: ["assignee-work", campaignId, userId],
    queryFn: () => getAssigneeWork(campaignId, userId),
  });

  const remove = useMutation({
    mutationFn: () =>
      removeAssignee(
        campaignId,
        userId,
        target === "" ? undefined : Number(target),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-assignees", campaignId] });
      void qc.invalidateQueries({
        queryKey: ["campaign-operator-stats", campaignId],
      });
      void qc.invalidateQueries({ queryKey: ["campaign-breakdown", campaignId] });
      onClose();
    },
  });

  const pending = work.data?.pending.total ?? 0;
  const needsTarget = pending > 0;
  const blocked = needsTarget && (target === "" || candidates.length === 0);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("campaigns.removeOperator.title", { name: displayName })}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel flex w-full max-w-lg flex-col gap-3">
        <h2 className="text-lg font-semibold">
          {t("campaigns.removeOperator.title", { name: displayName })}
        </h2>

        {work.isLoading && (
          <p className="text-sm text-nb-text-3">{t("common.loading")}</p>
        )}

        {work.data && !needsTarget && (
          <p className="text-sm text-nb-text-2">
            {t("campaigns.removeOperator.nothingPending")}
          </p>
        )}

        {work.data && needsTarget && (
          <>
            <p className="text-sm text-nb-text-2">
              {t("campaigns.removeOperator.pending", {
                count: pending,
                name: displayName,
              })}
            </p>

            {/* Said plainly, because it is the question everyone asks
                next and the answer is permanent. */}
            {work.data.completed_sessions > 0 && (
              <p className="rounded-md border border-nb-border bg-nb-bg/50 px-3 py-2 text-xs text-nb-text-3">
                {t("campaigns.removeOperator.completedStay", {
                  count: work.data.completed_sessions,
                  name: displayName,
                })}
              </p>
            )}

            {candidates.length === 0 ? (
              <p className="text-sm text-cvp-danger">
                {t("campaigns.removeOperator.noCandidates")}
              </p>
            ) : (
              <label className="space-y-1 text-xs text-nb-text-2">
                <span>{t("campaigns.removeOperator.chooseLabel")}</span>
                <select
                  className="input"
                  value={target}
                  onChange={(e) =>
                    setTarget(e.target.value === "" ? "" : Number(e.target.value))
                  }
                >
                  <option value="">
                    {t("campaigns.removeOperator.choosePlaceholder")}
                  </option>
                  {candidates.map((c) => (
                    <option key={c.user_id} value={c.user_id}>
                      {c.display_name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </>
        )}

        {remove.isError &&
          (() => {
            const clashes = handoverClashes(remove.error);
            if (clashes === null) {
              return (
                <p className="text-xs text-cvp-danger">
                  {t("campaigns.removeOperator.failed")}
                </p>
              );
            }
            return (
              <div
                className="space-y-1 rounded-md border border-cvp-danger/40 bg-cvp-danger/10 px-3 py-2 text-xs text-cvp-danger"
                data-testid="handover-clashes"
              >
                <p>{t("campaigns.removeOperator.clash", { count: clashes.length })}</p>
                <ul className="list-disc pl-4">
                  {clashes.map((c) => (
                    <li key={c.session_id}>
                      {formatDateTime(c.scheduled_for)} — {c.beneficiary}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost text-sm" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary bg-cvp-danger text-sm"
            disabled={blocked || remove.isPending || work.isLoading}
            onClick={() => remove.mutate()}
          >
            {remove.isPending
              ? t("common.saving")
              : t("campaigns.removeOperator.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
